<template>
  <form class="auth-step" @submit.prevent="$emit('submit')">
    <h4 class="mb-3">Verify OTP</h4>
    <p class="auth-step-text mb-2">We sent a 4-digit code to {{ phoneMask }}.</p>
    <p class="auth-step-text small mb-20">OTP expires in {{ otpCountdownText }}.</p>

    <div class="form-inner two mb-15">
      <label>4-digit OTP</label>
      <input
        :value="otp"
        type="text"
        inputmode="numeric"
        maxlength="4"
        class="otp-input"
        placeholder="0000"
        autocomplete="one-time-code"
        @input="onOtpInput"
      >
    </div>

    <p v-if="attemptsLeft > 0" class="attempts-text mb-15">Attempts left: {{ attemptsLeft }}</p>

    <div v-if="mockOtpHint" class="alert alert-info py-2 mb-15">
      Mock OTP for testing: <strong>{{ mockOtpHint }}</strong>
    </div>

    <button type="submit" class="primary-btn1 w-100 mb-20" :disabled="verifyBusy || otp.length !== 4">
      <span>{{ verifyBusy ? 'Verifying...' : 'Verify OTP' }}</span>
    </button>

    <div class="auth-otp-actions mb-15">
      <button type="button" class="btn btn-link btn-sm p-0" @click="$emit('back')">
        Back
      </button>

      <button
        type="button"
        class="btn btn-link btn-sm"
        :disabled="!canResend || resendBusy"
        @click="$emit('resend')"
      >
        {{ resendButtonText }}
      </button>
    </div>
  </form>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  mode: {
    type: String,
    required: true
  },
  phoneMask: {
    type: String,
    default: ''
  },
  otp: {
    type: String,
    default: ''
  },
  attemptsLeft: {
    type: Number,
    default: 0
  },
  otpCountdownSeconds: {
    type: Number,
    default: 0
  },
  resendCountdownSeconds: {
    type: Number,
    default: 0
  },
  canResend: {
    type: Boolean,
    default: false
  },
  status: {
    type: String,
    default: 'idle'
  },
  errorMessage: {
    type: String,
    default: ''
  },
  mockOtpHint: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['submit', 'update:otp', 'resend', 'back', 'switch-mode'])

const verifyBusy = computed(
  () => props.status === 'verifying_otp' || props.status === 'signing_up'
)
const resendBusy = computed(() => props.status === 'sending_otp')

const otpCountdownText = computed(() => {
  const totalSeconds = Math.max(0, Number(props.otpCountdownSeconds) || 0)
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
})

const resendButtonText = computed(() => {
  if (props.canResend) {
    return resendBusy.value ? 'Resending...' : 'Resend OTP'
  }

  return `Resend in ${props.resendCountdownSeconds}s`
})

const onOtpInput = (event) => {
  const digits = String(event.target.value || '').replace(/\D/g, '').slice(0, 4)
  emit('update:otp', digits)
}
</script>

<style scoped>
.auth-step-text {
  color: #666;
}

.otp-input {
  width: 100%;
  border: 1px solid #eee;
  border-radius: 8px;
  min-height: 52px;
  padding: 12px 14px;
  font-size: 24px;
  letter-spacing: 10px;
  text-align: center;
  font-weight: 600;
}

.attempts-text {
  color: #666;
  font-size: 13px;
}

.auth-otp-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
